/* eslint-disable no-alert */
/** @jsxImportSource @emotion/react */
import {
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
} from "@chakra-ui/react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Editor } from "@tiptap/react";
import dayjs from "dayjs";
import router from "next/router";
import { Fragment, useEffect, useState } from "react";
import { FaRegTrashAlt } from "react-icons/fa";
import { FiPlus } from "react-icons/fi";
import { HiArrowLeft } from "react-icons/hi";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from "recoil";
import { ContactApi } from "../../api/Contacts";
import { updateUser } from "../../api/User";
import FavoriteModalIcon from "../../assets/icon/favorite-modal";
import { ActiveEmailAtom } from "../../atoms/ActiveEmailAtom";
import EmailSelectorFamily, { EmailMode } from "../../atoms/EmailAtom";
import { EmailLinksAtom } from "../../atoms/EmailLinksAtom";
import { RecipientAtom } from "../../atoms/RecipientAtom";
import { CountedSavedSearchesAtom } from "../../atoms/SavedSearches";
import { ScheduleDateAtom } from "../../atoms/ScheduleDateAtom";
import { TagsAtom } from "../../atoms/TagsAtom";
import { TestDriveBannerAtom } from "../../atoms/TestDriveBannerAtom";
import { UserAtom } from "../../atoms/UserAtom";
import { WordsLengthAtom } from "../../atoms/WordsLengthAtom";
import { ICategoryType } from "../../generated/Api";
import useMediaQuery from "../../hook/useMediaQuery";
import useSetEmail from "../../hook/useSetEmail";
import { customTooltip, notSelect } from "../../styles/general";
import { IOption } from "../../types/CommonTypes";
import { IRecipientTag, Option } from "../../types/SavedSearchTypes";
import { ITagQuery } from "../../types/TagQueryTypes";
import { ITag } from "../../types/TagTypes";
import { checkTestDrive } from "../../utils/CheckTestDrive";
import { LEARNISTIC_UID } from "../../utils/constants";
import {
  localStorageGetItem,
  localStorageRemoveItem,
} from "../../utils/LocalStorage";
import { tagsOnTagQuery } from "../../utils/TagQuery";
import DateTimePicker from "../DateTimePicker";
import IconBerserkerMail from "../Icon";
import LoadingRow from "../LoadingRow";
import EmailSettings from "../Modal/container/EmailSettings/EmailSettings";
import { useModal } from "../Modal/Modal";
import MoreTagsTooltipContent from "../MoreTagsTooltipContent";
import SavedSearchSelect from "../SavedSearch/SavedSearchSelect";
import StyledButton from "../StyledButton";
import Tag from "../Tag";
import Warning from "./../Modal/container/Warning";
import LinkPreviews from "./LinkPreviews";
import RecipientsModal, { CurrentRecipientTagsAtom } from "./Modals/Recipients";
import ZeroLinksWarningModal from "./Modals/ZeroLinksWarning";

const DrawerOverlay = styled.div<{ isDrawerOpen: boolean }>`
  width: 100vw;
  height: 100vh;
  min-height: 100%;

  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: ${(props) => (props.isDrawerOpen ? "999" : "-999")};

  background: rgba(0, 0, 0, 0.16);

  opacity: ${(props) => (props.isDrawerOpen ? "1" : "0")};
  transition: opacity 0.3s ease-in-out;

  cursor: pointer;
`;

const DrawerContent = styled.div<{ isDrawerOpen: boolean }>`
  width: 400px;
  height: 100vh;
  min-height: 100%;

  padding-top: 52px;
  padding-bottom: 52px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  background: #fff;

  border-left: 1px solid #eaeaea;

  position: absolute;
  top: 0;
  bottom: 0;
  right: ${(props) => (props.isDrawerOpen ? "0" : "-400px")};
  z-index: 999;

  transition: right 0.3s ease-in-out;
`;

export const CloseButtonWrapper = styled.div`
  width: 40px;
  height: 40px;

  border-radius: 50%;

  background: #f5f5f5;

  display: flex;
  align-items: center;
  justify-content: center;

  position: absolute;
  top: 8px;
  right: 8px;

  padding: 10px;

  cursor: pointer;

  svg {
    width: 100%;
    height: 100%;
    color: #868482;
  }
`;

const Wrapper = styled.div<{ showTestDrive: boolean }>`
  width: 380px;
  height: ${(props) =>
    props.showTestDrive ? "calc(100% - 83px - 75px)" : "calc(100% - 83px)"};
  background: #ffffff;
  position: fixed;
  top: ${(props) => (props.showTestDrive ? "calc(83px + 75px)" : "83px")};
  right: 0;
  border-left: 1px solid #dddddd;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-y: auto;
`;

const FindRecipients = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  padding: 26px 31px 0px 31px;
  font-weight: 500;
`;

const RecipientHeader = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
`;

const SavedSearchesSelectContainer = styled.div`
  width: 100%;
`;

const TagsContainer = styled.div`
  margin: 10px 0px;
  display: flex;
  flex-wrap: wrap;
`;

const AutoCompleteInputContainer = styled.div`
  margin-top: 5px;
`;

const EditRecipientsButton = styled.span`
  color: #fb972e;
  font-size: 16px;
  cursor: pointer;
`;

const SelectedCount = styled.span`
  font-size: 15px;
  color: #868482;
  font-weight: 400;
`;

const Button = styled.div`
  display: flex;
  flex-direction: row;
  cursor: pointer;
  color: #fb972e;
  ${notSelect}
  :hover {
    opacity: 0.7;
  }
`;

const ScheduleDiv = styled.div`
  padding: 5px 31px 0px 31px;
`;

const ScheduleDivHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  font-size: 16px;
  font-weight: 600;
  margin-bottom: 5px;
  margin-top: 10px;
`;

const TooltipTags = styled.div`
  display: flex;
  flex-direction: column;

  & span {
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 0px;
    }
  }
`;

const SendButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const AllContactsContainer = styled.div`
  border: 1px solid #ccc;
  background-color: #f4f4f4;
  border-radius: 5px;
  margin-top: 5px;
  display: flex;
  align-items: center;
  width: 100%;
  padding-left: 20px;
  height: 38px;
  font-size: 16px;
`;

const EditOrResetContainer = styled.div`
  display: flex;

  > div:last-child {
    margin-left: 15px;
  }
`;

const SelectedRecipients = (props: {
  isLoadingRecipient: boolean;
}): JSX.Element => {
  const modal = useModal();

  const [selectedRecipient, setSelectedRecipient] =
    useRecoilState(RecipientAtom);
  const [selectedTags, setSelectedTags] = useState<ITag[]>();
  const tagsPool = useRecoilValue(TagsAtom);
  const setActiveEmail = useSetRecoilState(ActiveEmailAtom);
  const countedSavedSearches = useRecoilValue(CountedSavedSearchesAtom);
  const [selectedSavedSearch, setSelectedSavedSearch] = useState<IOption>({
    name: "",
    value: "",
  });
  const [autoCompleteSuggestions, setAutoCompleteSuggestions] = useState<
    IOption[]
  >([]);
  const setRecipientTags = useSetRecoilState<IRecipientTag[]>(
    CurrentRecipientTagsAtom
  );
  const resetCurrentRecipientTags = useResetRecoilState(
    CurrentRecipientTagsAtom
  );
  const { isLoadingRecipient } = props;

  // Update the suggestions
  useEffect(() => {
    const suggestions: IOption[] = countedSavedSearches.map((savedSearch) => ({
      value: savedSearch.id,
      name: savedSearch.name,
      icon: "tag",
      suffix: `${savedSearch.contactsCount}`,
    }));

    setAutoCompleteSuggestions(suggestions);

    if (selectedSavedSearch.value) {
      handleSelectedSearch(selectedSavedSearch);
    }
  }, [countedSavedSearches]);

  useEffect(() => {
    if (!selectedRecipient.tagQuery) return;
    if (!tagsPool.length) return;

    const selectedTagsIds = tagsOnTagQuery(selectedRecipient.tagQuery);
    const uniqueSelectedTagsIds = Array.from(new Set(selectedTagsIds));

    setSelectedTags(
      tagsPool.filter(({ _id }) =>
        uniqueSelectedTagsIds.find((tagId) => tagId === _id)
      )
    );
  }, [selectedRecipient.tagQuery, tagsPool]);

  useEffect(() => {
    if (selectedRecipient.searchName)
      setSelectedSavedSearch({
        name: selectedRecipient.searchName,
        value: selectedRecipient.searchId,
      });
    else setSelectedSavedSearch({ name: "", value: "" });
  }, [selectedRecipient.searchName]);

  function handleSelectedSearch(savedSearchOption: IOption) {
    if (savedSearchOption.value === "" || savedSearchOption.name === "") {
      handleResetSearch();
    }

    setSelectedSavedSearch({
      name: savedSearchOption.name,
      value: savedSearchOption.value,
    });

    const foundSelectedSavedSearch = countedSavedSearches.find(
      ({ id }) => savedSearchOption.value === id
    );

    if (!foundSelectedSavedSearch) return;

    setSelectedRecipient((prevSelected) => ({
      ...prevSelected,
      count: foundSelectedSavedSearch.contactsCount,
      tagQuery: foundSelectedSavedSearch.search,
      setted: "savedSearch",
      footerId: foundSelectedSavedSearch.footerId,
      searchName: foundSelectedSavedSearch.name,
      searchId: foundSelectedSavedSearch.id,
      showOnArchive: foundSelectedSavedSearch.publicArchive,
    }));

    setActiveEmail((prevState) => ({
      ...prevState,
      labels: foundSelectedSavedSearch.labels,
      searchId: foundSelectedSavedSearch.id,
    }));
  }

  function handleResetSearch() {
    localStorageRemoveItem("ATOM_CURRENT_RECIPIENTS");
    localStorageRemoveItem("ATOM_RECIPIENT");
    setSelectedSavedSearch({
      name: "",
      value: "",
    });
    setSelectedRecipient({
      ...selectedRecipient,
      ...(selectedRecipient.searchId && { footerId: null }),
      count: null,
      searchId: null,
      searchName: undefined,
      showOnArchive: undefined,
      setted: false,
      tagQuery: null,
    });
    resetCurrentRecipientTags();
    setActiveEmail((prevState) => ({
      ...prevState,
      labels: [],
    }));
  }

  async function handleRemoveTag(tagId: string) {
    let tagQuery: typeof selectedRecipient.tagQuery = JSON.parse(
      JSON.stringify(selectedRecipient.tagQuery)
    );
    if (!tagQuery) return;

    tagQuery = {
      ...tagQuery,
      tags: tagQuery.tags.filter((t) => t !== tagId),
    } as ITagQuery;

    let currentNext = tagQuery.next;
    let previous = tagQuery;

    while (currentNext) {
      currentNext.query = {
        ...currentNext.query,
        tags: currentNext.query.tags.filter((t) => t !== tagId),
      };

      if (currentNext.query.tags.length === 0) {
        previous.next = currentNext.query.next;
      } else {
        previous = currentNext.query;
      }

      currentNext = currentNext.query.next;
    }

    if (tagQuery.tags.length === 0)
      tagQuery = tagQuery.next ? tagQuery.next.query : null;

    if (!tagQuery) {
      handleResetSearch();
      return;
    }

    const [contactsCount] = await ContactApi.countContactsByTagQueries([
      tagQuery,
    ]);

    tagQuery = { ...tagQuery };

    setSelectedRecipient((r) => ({ ...r, tagQuery, count: contactsCount }));

    setRecipientTags((recipients) => {
      const recipientsFilter = recipients.map((r) => ({
        ...r,
        tags: r.tags.filter((t) => t._id !== tagId),
      }));
      return recipientsFilter.filter((r) => r.tags.length !== 0);
    });
  }

  return (
    <FindRecipients>
      <RecipientHeader>
        <span>
          <span
            style={{ fontSize: "16px", fontWeight: 600, marginRight: "5px" }}
          >
            Contacts
          </span>
          {selectedRecipient.setted && !isLoadingRecipient && (
            <SelectedCount>({selectedRecipient.count} selected)</SelectedCount>
          )}
        </span>
        {!isLoadingRecipient && (
          <EditRecipientsButton>
            {selectedRecipient.setted === "tagquery" ? (
              //This ternary below represents the "All Contacts" case:
              //if this option is selected, we should display "Reset" button
              selectedRecipient.tagQuery?.operator === "ALL" &&
              selectedRecipient.tagQuery?.next === undefined ? (
                <Button onClick={handleResetSearch}>
                  <IconBerserkerMail name="reset" />
                  <span
                    css={css`
                      font-size: 16px;
                      font-weight: 600;
                      margin-left: 5px;
                      ${notSelect}
                    `}
                  >
                    Reset
                  </span>
                </Button>
              ) : (
                <EditOrResetContainer>
                  <Button
                    onClick={() => {
                      modal.pushModal(<RecipientsModal />, "Contacts");
                    }}
                  >
                    <IconBerserkerMail name="pen" />
                    <span
                      css={css`
                        font-size: 16px;
                        font-weight: 600;
                        margin-left: 5px;
                        ${notSelect}
                      `}
                    >
                      Edit
                    </span>
                  </Button>

                  <Button onClick={handleResetSearch}>
                    <IconBerserkerMail name="reset" />
                    <span
                      css={css`
                        font-size: 16px;
                        font-weight: 600;
                        margin-left: 5px;
                        ${notSelect}
                      `}
                    >
                      Reset
                    </span>
                  </Button>
                </EditOrResetContainer>
              )
            ) : (
              <>
                {selectedRecipient.setted === "savedSearch" ? (
                  <Button onClick={handleResetSearch}>
                    <IconBerserkerMail name="reset" />
                    <span
                      css={css`
                        font-size: 16px;
                        font-weight: 600;
                        margin-left: 5px;
                        ${notSelect}
                      `}
                    >
                      Reset
                    </span>
                  </Button>
                ) : (
                  <Button
                    css={css`
                      font-size: 16px;
                      font-weight: 600;
                      display: flex;
                      align-items: center;
                    `}
                    onClick={() => {
                      modal.pushModal(<RecipientsModal />, "Contacts");
                    }}
                  >
                    <FiPlus width="16px" height="16px" color="#fb972e" /> Select
                  </Button>
                )}
              </>
            )}
          </EditRecipientsButton>
        )}
      </RecipientHeader>
      {isLoadingRecipient ? (
        <LoadingRow />
      ) : (
        <SavedSearchesSelectContainer>
          {selectedRecipient.setted === "tagquery" ? (
            <TagsContainer>
              {selectedTags ? (
                !selectedTags.length ? (
                  <AllContactsContainer>All Contacts</AllContactsContainer>
                ) : (
                  selectedTags.map((tag, index) => {
                    if (index < 2) {
                      return (
                        <Tag
                          deleteTag={() => handleRemoveTag(tag._id as string)}
                          customStyle={{ margin: "5px" }}
                          tag={tag}
                        />
                      );
                    } else if (index === 2 && selectedTags.length === 3) {
                      return (
                        <Tag
                          deleteTag={() => handleRemoveTag(tag._id as string)}
                          customStyle={{ margin: "5px" }}
                          tag={tag}
                        />
                      );
                    } else if (index === 2) {
                      return (
                        <div data-tip data-for="tag">
                          <Tag
                            customStyle={{ margin: "5px" }}
                            tag={{
                              _id: "",
                              name: `+${selectedTags.length - 2} more`,
                              userUid: "",
                              category: "",
                              type: ICategoryType.User,
                              categoryId: "",
                              createdAt: new Date(),
                            }}
                            noPopUp
                          />

                          <ReactTooltip
                            id="tag"
                            place="bottom"
                            effect="solid"
                            backgroundColor="#333333"
                            css={customTooltip}
                          >
                            <TooltipTags>
                              <MoreTagsTooltipContent
                                selectedTags={selectedTags}
                              />
                            </TooltipTags>
                          </ReactTooltip>
                        </div>
                      );
                    }
                  })
                )
              ) : null}
            </TagsContainer>
          ) : (
            <AutoCompleteInputContainer>
              <SavedSearchSelect
                options={autoCompleteSuggestions}
                onOptionSelect={handleSelectedSearch}
                selectedOption={selectedSavedSearch}
                placeholder="Type or select saved search"
              />
            </AutoCompleteInputContainer>
          )}
        </SavedSearchesSelectContainer>
      )}
    </FindRecipients>
  );
};

const UpperDiv = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  margin-left: -1px;
  height: 100%;

  @media screen and (max-width: 1080px) {
    width: 100%;
    overflow-y: auto;
    margin: 0;
  }
`;

const LowerDiv = styled.div`
  display: flex;
  flex-direction: column;
  padding: 25px;
  margin-left: -1px;

  @media screen and (max-width: 1080px) {
    width: 100%;
    padding: 24px 31px;
    margin: 0;
  }
`;

const OptionDefault = {
  id: "",
  name: "",
};

interface Props {
  mode: EmailMode;
  sendEmailHandler: () => void;
  scheduleEmailHandler: () => void;
  isLoadingRecipient?: boolean;
  onClose: () => void;
  isOpen: boolean;
  editor: Editor;
}

const ScheduleBar = (props: Props): JSX.Element => {
  const {
    scheduleEmailHandler,
    sendEmailHandler,
    mode,
    isLoadingRecipient,
    isOpen,
    onClose,
    editor,
  } = props;
  const isLargerThan1080 = useMediaQuery("(min-width: 1080px)");
  const isLargerThan640 = useMediaQuery("(min-width: 640px)");
  const words = useRecoilValue(WordsLengthAtom);
  const email = useRecoilValue(EmailSelectorFamily({ mode }));
  const testDriveBanner = useRecoilValue(TestDriveBannerAtom);
  const recipient = useRecoilValue(RecipientAtom);
  const [scheduleDate, setScheduleDate] = useRecoilState(ScheduleDateAtom);
  const [time, setTime] = useState<string | undefined>();
  const [calendarKey, setCalendarKey] = useState<string>("");
  const links = useRecoilValue(EmailLinksAtom);
  const modal = useModal();
  const [user, setUser] = useRecoilState(UserAtom);
  const [isSendingLoading, setIsSendingLoading] = useState(false);
  const [listValue, setListValue] = useState<Option>(OptionDefault);
  const { resetEmailContent } = useSetEmail(mode, editor);

  function getDateAndTime(fromDate: Date) {
    const offset = fromDate.getTimezoneOffset() * 60 * 1000;
    const localTime = new Date(fromDate.getTime() - offset);
    const [day, timeAndPeriod] = localTime.toISOString().split("T");
    const [hour] = timeAndPeriod.split("Z");

    return [day, hour];
  }

  function handleClear() {
    modal.pushModal(
      <Warning
        icon={"./icons/delete.svg"}
        title="Clear Email Content"
        description="Are you sure you wish to clear this email content?"
        buttonsName={["No", "Yes"]}
        onOkClicked={() => {
          resetEmailContent.current();
          modal.popModal();
        }}
        onCancelClicked={() => modal.popModal()}
      />
    );
  }

  function handleDateSelect(selectedDate: Date) {
    const [day, hour] = getDateAndTime(selectedDate);

    if (time && selectedDate.getMinutes() !== 21) {
      const mountedDateTime = dayjs(`${day} ${time}`).toDate();

      setScheduleDate(mountedDateTime);
    } else {
      let newMinutes = parseInt(hour.split(":")[1], 10);
      let newSelectedDate = new Date(selectedDate);

      const atualMinutes = newMinutes;
      if (newMinutes % 5 === 0 || newMinutes === 21) {
        setTime(hour);
        setScheduleDate(selectedDate);
      } else {
        while (newMinutes % 5 !== 0) {
          newMinutes += 1;
        }

        newSelectedDate = dayjs(newSelectedDate)
          .add(newMinutes - atualMinutes, "minutes")
          .toDate();

        const [, newHour] = getDateAndTime(newSelectedDate);

        setTime(newHour);
        setScheduleDate(newSelectedDate);
      }
    }
  }

  async function handleTimeSelect(selectedDate: Date) {
    setScheduleDate(selectedDate);

    //Warning about favoriteTime with a modal
    if (!user.options.doNotShowFavoriteTimesAdvice) {
      modal.pushModal(
        <Warning
          fontAwesomeIcon={() => (
            <div
              css={css`
                margin-bottom: 32px;
              `}
            >
              <FavoriteModalIcon />
            </div>
          )}
          title="Introducing Favorite Schedules"
          description="To get started, please proceed to your email settings area by tapping on the Setup button."
          buttonsName={["Skip", "Setup"]}
          onOkClicked={() => {
            modal.popModal();
            modal.pushModal(<EmailSettings />, "Email Settings", false, true);
          }}
          onCancelClicked={() => {
            modal.popModal();
          }}
        />
      );

      const newUser = {
        ...user,
        options: {
          ...user.options,
          doNotShowFavoriteTimesAdvice: true,
        },
      };

      const isSuccess = await updateUser(newUser);

      if (isSuccess) setUser(newUser);
    }
  }

  async function handleFinishClick() {
    if (!recipient.tagQuery && !listValue.id) {
      toast("You must do a tag query or select an email list", {
        type: "info",
      });
      return;
    }

    if (!email.subject.trim()) {
      toast("You need a subject and body", { type: "info" });
      return;
    }

    if (links.tooManyLinks) {
      toast("You have more than three links", { type: "error" });
      return;
    }
    let hasInvalid = false;
    links.links.map((link) => {
      if (!link.valid) hasInvalid = true;
    });
    if (hasInvalid) {
      toast("You have a broken link", { type: "error" });
      return;
    }

    if (listValue.id) {
      setListValue(OptionDefault);
    }

    if (scheduleDate) {
      const now = dayjs();
      if (dayjs(scheduleDate).add(60, "seconds").isBefore(now)) {
        toast("Invalid scheduling date ", { type: "warning" });
      } else if (dayjs(scheduleDate).minute() === 21) {
        toast("You must select a time ", { type: "warning" });
      } else {
        scheduleEmailHandler();
      }
    } else {
      sendEmailHandler();
    }
  }

  function handleZeroLinksModal(dontShowMe: boolean) {
    //In the future, I think it's better to set this on DB.
    if (dontShowMe) {
      localStorage.setItem(
        "@berserkermail/dontshownolinksaddedreminder",
        "true"
      );
    }
    modal.popModal();
  }

  function handleScheduleOrSend() {
    const dontRemindMe = localStorage.getItem(
      "@berserkermail/dontshownolinksaddedreminder"
    );

    const hasLearnisticLink = links.links.filter((link) =>
      link.link.toLowerCase().includes("learnistic.me")
    );

    if (hasLearnisticLink.length && user.userUid !== LEARNISTIC_UID) {
      modal.pushModal(
        <Warning
          buttonsName={["Ok"]}
          title=""
          description={`Learnistic.me deep links cannot be sent directly. You need to wrap them with RedirectMe.io links on your own domain or another link wrapping solution.\n\nThis is done to protect the Learnistic.me domain reputation. This was a requirement that we made you aware of when turning Deep Links on for your Learnistic app. Thank you for your cooperation in this matter.`}
          icon={"./icons/modal/block-user.svg"}
          onOkClicked={() => {
            modal.popAll();
          }}
          onCancelClicked={() => {
            modal.popAll();
          }}
        />
      );
      return;
    }

    if (!dontRemindMe && links.links.length === 0) {
      modal.pushModal(
        <ZeroLinksWarningModal
          mode={scheduleDate ? "schedule" : "send"}
          onCancel={() => modal.popModal()}
          onConfirm={(remindInFuture) => {
            handleZeroLinksModal(remindInFuture);
            handleFinishClick();
          }}
        />
      );
    } else {
      handleFinishClick();
    }
  }

  if (isLargerThan1080) {
    return (
      <Wrapper showTestDrive={testDriveBanner.show}>
        <UpperDiv>
          <SelectedRecipients isLoadingRecipient={isLoadingRecipient} />
          <ScheduleDiv>
            <ScheduleDivHeader>
              Schedule for Later
              {scheduleDate && (
                <Button
                  onClick={() => {
                    setScheduleDate(undefined);
                    setCalendarKey(new Date().getTime() + "");
                    setTime(undefined);
                  }}
                >
                  <IconBerserkerMail name="reset" />
                  <span
                    css={css`
                      font-size: 16px;
                      font-weight: 600;
                      margin-left: 5px;
                      ${notSelect}
                    `}
                  >
                    Reset
                  </span>
                </Button>
              )}
            </ScheduleDivHeader>
            <DateTimePicker
              date={scheduleDate}
              onDateSelect={handleDateSelect}
              onTimeSelect={handleTimeSelect}
              calendarKey={calendarKey}
              setCalendarKey={setCalendarKey}
              showDatePicker
              showTimePicker
              input
            />
          </ScheduleDiv>

          <ScheduleDiv>
            <>
              {user?.options?.showPreviewLink && (
                <ScheduleDivHeader>Link Preview</ScheduleDivHeader>
              )}
              <LinkPreviews
                areLinksReady={(isLinksLoading) =>
                  setIsSendingLoading(isLinksLoading)
                }
                mode={mode}
              />
            </>
          </ScheduleDiv>
        </UpperDiv>
        <LowerDiv>
          {mode === "new-email" ? (
            <SendButtonContainer>
              <StyledButton
                disable={email.subject === "" && words === 0}
                inverted
                onClick={handleClear}
                css={css`
                  width: fit-content;
                  margin-right: 12px;
                  height: 46px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  padding: 0px 16px;
                `}
              >
                <FaRegTrashAlt color="#555555" />
              </StyledButton>

              <StyledButton
                disable={email.subject === "" && words === 0}
                inverted
                onClick={() => toast("Saved", { type: "success" })}
                css={css`
                  width: 45%;
                  margin-right: 12px;
                  height: 46px;
                `}
              >
                Save Draft
              </StyledButton>

              {!links.tooManyLinks &&
                process.browser &&
                !localStorageGetItem("ADMIN_UID") && (
                  <StyledButton
                    isLoading={isSendingLoading}
                    css={css`
                      width: 45%;
                      height: 46px;
                    `}
                    onClick={async () => {
                      if (checkTestDrive(user?.billing)) {
                        setIsSendingLoading(true);
                        const { exists, contact } =
                          await ContactApi.getTestDriveContact();
                        setIsSendingLoading(false);

                        let description = "";

                        if (exists) {
                          description = `This email will be sent to ${contact.email}`;
                        } else {
                          description = `As you are in a test drive, you need to set a test drive contact in order to send emails, please click in the button below to access your contact page and set one`;
                        }

                        modal.pushModal(
                          <Warning
                            icon={"./icons/modal/email.svg"}
                            title={`Test Drive Account`}
                            description={description}
                            buttonsName={
                              exists ? ["Cancel", "Send"] : ["Cancel", "Ok"]
                            }
                            onOkClicked={() => {
                              if (exists) {
                                modal.popModal();
                                handleScheduleOrSend();
                              } else {
                                modal.popModal();
                                router.replace("/contacts");
                              }
                            }}
                            onCancelClicked={() => {
                              modal.popAll();
                            }}
                          />
                        );
                      } else {
                        handleScheduleOrSend();
                      }
                    }}
                    disable={!recipient.count}
                  >
                    {scheduleDate ? "Schedule" : "Send"}
                  </StyledButton>
                )}
            </SendButtonContainer>
          ) : (
            <>
              {email.type === "draft" ? (
                <SendButtonContainer>
                  <StyledButton
                    inverted
                    onClick={() => toast("Saved", { type: "success" })}
                    // onClick={draftEmailHandler}
                    css={css`
                      width: 45%;
                      margin-right: 12px;
                      height: 46px;
                    `}
                  >
                    Save Draft
                  </StyledButton>

                  {!links.tooManyLinks &&
                    process.browser &&
                    !localStorageGetItem("ADMIN_UID") && (
                      <StyledButton
                        isLoading={isSendingLoading}
                        css={css`
                          width: 45%;
                          height: 46px;
                        `}
                        onClick={async () => {
                          if (checkTestDrive(user?.billing)) {
                            setIsSendingLoading(true);
                            const { exists, contact } =
                              await ContactApi.getTestDriveContact();
                            setIsSendingLoading(false);

                            let description = "";

                            if (exists) {
                              description = `This email will be sent to ${contact.email}`;
                            } else {
                              description = `As you are in a test drive, you need to set a test drive contact in order to send emails, please click in the button below to access your contact page and set one`;
                            }

                            modal.pushModal(
                              <Warning
                                icon={"./icons/modal/email.svg"}
                                title={`Test Drive Account`}
                                description={description}
                                buttonsName={
                                  exists ? ["Cancel", "Send"] : ["Cancel", "Ok"]
                                }
                                onOkClicked={() => {
                                  if (exists) {
                                    handleScheduleOrSend();
                                    modal.popAll();
                                  } else {
                                    modal.popModal();
                                    router.replace("/contacts");
                                  }
                                }}
                                onCancelClicked={() => modal.popAll()}
                              />
                            );
                          } else {
                            handleScheduleOrSend();
                          }
                        }}
                        disable={!recipient.count}
                      >
                        {scheduleDate ? "Schedule" : "Send"}
                      </StyledButton>
                    )}
                </SendButtonContainer>
              ) : (
                <StyledButton onClick={handleFinishClick}>
                  {scheduleDate ? "Reschedule" : "Send Now"}
                </StyledButton>
              )}
            </>
          )}
        </LowerDiv>
      </Wrapper>
    );
  }

  if (isLargerThan640) {
    return (
      <>
        <DrawerOverlay isDrawerOpen={isOpen} onClick={onClose} />
        <DrawerContent isDrawerOpen={isOpen}>
          <CloseButtonWrapper onClick={onClose}>
            <HiArrowLeft />
          </CloseButtonWrapper>
          <UpperDiv>
            <SelectedRecipients isLoadingRecipient={isLoadingRecipient} />
            <ScheduleDiv>
              <ScheduleDivHeader>
                Schedule for Later
                {scheduleDate && (
                  <Button
                    onClick={() => {
                      setScheduleDate(undefined);
                      setCalendarKey(new Date().getTime() + "");
                      setTime(undefined);
                    }}
                  >
                    <IconBerserkerMail name="reset" />
                    <span
                      css={css`
                        font-size: 16px;
                        font-weight: 600;
                        margin-left: 5px;
                        ${notSelect}
                      `}
                    >
                      Reset
                    </span>
                  </Button>
                )}
              </ScheduleDivHeader>
              <DateTimePicker
                date={scheduleDate}
                onDateSelect={handleDateSelect}
                onTimeSelect={handleTimeSelect}
                calendarKey={calendarKey}
                setCalendarKey={setCalendarKey}
                showDatePicker
                showTimePicker
                input
              />
            </ScheduleDiv>

            <ScheduleDiv>
              <>
                {user?.options?.showPreviewLink && (
                  <ScheduleDivHeader>Link Preview</ScheduleDivHeader>
                )}
                <LinkPreviews
                  areLinksReady={(isLinksLoading) =>
                    setIsSendingLoading(isLinksLoading)
                  }
                  mode={mode}
                />
              </>
            </ScheduleDiv>
          </UpperDiv>
          <LowerDiv>
            {mode === "new-email" ? (
              <SendButtonContainer>
                <StyledButton
                  disable={email.subject === "" && words === 0}
                  inverted
                  onClick={handleClear}
                  css={css`
                    width: fit-content;
                    margin-right: 12px;
                    height: 46px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0px 16px;
                  `}
                >
                  <FaRegTrashAlt color="#555555" />
                </StyledButton>

                <StyledButton
                  disable={email.subject === "" && words === 0}
                  inverted
                  onClick={() => toast("Saved", { type: "success" })}
                  css={css`
                    width: 45%;
                    margin-right: 12px;
                    height: 46px;
                  `}
                >
                  Save Draft
                </StyledButton>

                {!links.tooManyLinks &&
                  process.browser &&
                  !localStorageGetItem("ADMIN_UID") && (
                    <StyledButton
                      isLoading={isSendingLoading}
                      css={css`
                        width: 45%;
                        height: 46px;
                      `}
                      onClick={async () => {
                        if (checkTestDrive(user?.billing)) {
                          setIsSendingLoading(true);
                          const { exists, contact } =
                            await ContactApi.getTestDriveContact();
                          setIsSendingLoading(false);

                          let description = "";

                          if (exists) {
                            description = `This email will be sent to ${contact.email}`;
                          } else {
                            description = `As you are in a test drive, you need to set a test drive contact in order to send emails, please click in the button below to access your contact page and set one`;
                          }

                          modal.pushModal(
                            <Warning
                              icon={"./icons/modal/email.svg"}
                              title={`Test Drive Account`}
                              description={description}
                              buttonsName={
                                exists ? ["Cancel", "Send"] : ["Cancel", "Ok"]
                              }
                              onOkClicked={() => {
                                if (exists) {
                                  modal.popModal();
                                  handleScheduleOrSend();
                                } else {
                                  modal.popModal();
                                  router.replace("/contacts");
                                }
                              }}
                              onCancelClicked={() => {
                                modal.popAll();
                              }}
                            />
                          );
                        } else {
                          handleScheduleOrSend();
                        }
                      }}
                      disable={!recipient.count}
                    >
                      {scheduleDate ? "Schedule" : "Send"}
                    </StyledButton>
                  )}
              </SendButtonContainer>
            ) : (
              <>
                {email.type === "draft" ? (
                  <SendButtonContainer>
                    <StyledButton
                      inverted
                      onClick={() => toast("Saved", { type: "success" })}
                      // onClick={draftEmailHandler}
                      css={css`
                        width: 45%;
                        margin-right: 12px;
                        height: 46px;
                      `}
                    >
                      Save Draft
                    </StyledButton>

                    {!links.tooManyLinks &&
                      process.browser &&
                      !localStorageGetItem("ADMIN_UID") && (
                        <StyledButton
                          isLoading={isSendingLoading}
                          css={css`
                            width: 45%;
                            height: 46px;
                          `}
                          onClick={async () => {
                            if (checkTestDrive(user?.billing)) {
                              setIsSendingLoading(true);
                              const { exists, contact } =
                                await ContactApi.getTestDriveContact();
                              setIsSendingLoading(false);

                              let description = "";

                              if (exists) {
                                description = `This email will be sent to ${contact.email}`;
                              } else {
                                description = `As you are in a test drive, you need to set a test drive contact in order to send emails, please click in the button below to access your contact page and set one`;
                              }

                              modal.pushModal(
                                <Warning
                                  icon={"./icons/modal/email.svg"}
                                  title={`Test Drive Account`}
                                  description={description}
                                  buttonsName={
                                    exists
                                      ? ["Cancel", "Send"]
                                      : ["Cancel", "Ok"]
                                  }
                                  onOkClicked={() => {
                                    if (exists) {
                                      handleScheduleOrSend();
                                      modal.popAll();
                                    } else {
                                      modal.popModal();
                                      router.replace("/contacts");
                                    }
                                  }}
                                  onCancelClicked={() => modal.popAll()}
                                />
                              );
                            } else {
                              handleScheduleOrSend();
                            }
                          }}
                          disable={!recipient.count}
                        >
                          {scheduleDate ? "Schedule" : "Send"}
                        </StyledButton>
                      )}
                  </SendButtonContainer>
                ) : (
                  <StyledButton onClick={handleFinishClick}>
                    {scheduleDate ? "Reschedule" : "Send Now"}
                  </StyledButton>
                )}
              </>
            )}
          </LowerDiv>
        </DrawerContent>
      </>
    );
  }

  return (
    <Modal size="full" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <IconButton
          onClick={onClose}
          background="#fb972e"
          color="#fff"
          position="absolute"
          top={2}
          left={6}
          aria-label="Search database"
          icon={<HiArrowLeft color="gray.400" />}
        />
        <ModalBody padding="64px 0 32px">
          <SelectedRecipients isLoadingRecipient={isLoadingRecipient} />
          <ScheduleDiv>
            <ScheduleDivHeader>
              Schedule for Later
              {scheduleDate && (
                <Button
                  onClick={() => {
                    setScheduleDate(undefined);
                    setCalendarKey(new Date().getTime() + "");
                    setTime(undefined);
                  }}
                >
                  <IconBerserkerMail name="reset" />
                  <span
                    css={css`
                      font-size: 16px;
                      font-weight: 600;
                      margin-left: 5px;
                      ${notSelect}
                    `}
                  >
                    Reset
                  </span>
                </Button>
              )}
            </ScheduleDivHeader>
            <DateTimePicker
              date={scheduleDate}
              onDateSelect={handleDateSelect}
              onTimeSelect={handleTimeSelect}
              calendarKey={calendarKey}
              setCalendarKey={setCalendarKey}
              showDatePicker
              showTimePicker
              input
            />
          </ScheduleDiv>

          <ScheduleDiv>
            <>
              {user?.options?.showPreviewLink && (
                <ScheduleDivHeader>Link Preview</ScheduleDivHeader>
              )}
              <LinkPreviews
                areLinksReady={(isLinksLoading) =>
                  setIsSendingLoading(isLinksLoading)
                }
                mode={mode}
              />
            </>
          </ScheduleDiv>
        </ModalBody>

        <ModalFooter
          w="100%"
          paddingX={6}
          display="flex"
          alignItems="center"
          justifyContent="center"
          gridGap="16px"
          flexWrap="wrap"
          borderTop="1px solid #e5e5e5"
        >
          {mode === "new-email" ? (
            <Fragment>
              <StyledButton
                disable={email.subject === "" && words === 0}
                inverted
                onClick={handleClear}
                css={css`
                  width: 46px;
                  height: 46px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  padding: 0px 16px;
                `}
              >
                <FaRegTrashAlt color="#555555" />
              </StyledButton>

              <StyledButton
                disable={email.subject === "" && words === 0}
                inverted
                onClick={() => toast("Saved", { type: "success" })}
                css={css`
                  width: 124px;
                  height: 46px;
                `}
              >
                Save Draft
              </StyledButton>

              {!links.tooManyLinks &&
                process.browser &&
                !localStorageGetItem("ADMIN_UID") && (
                  <StyledButton
                    isLoading={isSendingLoading}
                    css={css`
                      width: 112px;
                      height: 46px;
                    `}
                    onClick={async () => {
                      if (checkTestDrive(user?.billing)) {
                        setIsSendingLoading(true);
                        const { exists, contact } =
                          await ContactApi.getTestDriveContact();
                        setIsSendingLoading(false);

                        let description = "";

                        if (exists) {
                          description = `This email will be sent to ${contact.email}`;
                        } else {
                          description = `As you are in a test drive, you need to set a test drive contact in order to send emails, please click in the button below to access your contact page and set one`;
                        }

                        modal.pushModal(
                          <Warning
                            icon={"./icons/modal/email.svg"}
                            title={`Test Drive Account`}
                            description={description}
                            buttonsName={
                              exists ? ["Cancel", "Send"] : ["Cancel", "Ok"]
                            }
                            onOkClicked={() => {
                              if (exists) {
                                modal.popModal();
                                handleScheduleOrSend();
                              } else {
                                modal.popModal();
                                router.replace("/contacts");
                              }
                            }}
                            onCancelClicked={() => {
                              modal.popAll();
                            }}
                          />
                        );
                      } else {
                        handleScheduleOrSend();
                      }
                    }}
                    disable={!recipient.count}
                  >
                    {scheduleDate ? "Schedule" : "Send"}
                  </StyledButton>
                )}
            </Fragment>
          ) : (
            <Fragment>
              {email.type === "draft" ? (
                <Fragment>
                  <StyledButton
                    inverted
                    onClick={() => toast("Saved", { type: "success" })}
                    // onClick={draftEmailHandler}
                    css={css`
                      width: 124px;
                      height: 46px;
                    `}
                  >
                    Save Draft
                  </StyledButton>

                  {!links.tooManyLinks &&
                    process.browser &&
                    !localStorageGetItem("ADMIN_UID") && (
                      <StyledButton
                        isLoading={isSendingLoading}
                        css={css`
                          width: 124px;
                          height: 46px;
                        `}
                        onClick={async () => {
                          if (checkTestDrive(user?.billing)) {
                            setIsSendingLoading(true);
                            const { exists, contact } =
                              await ContactApi.getTestDriveContact();
                            setIsSendingLoading(false);

                            let description = "";

                            if (exists) {
                              description = `This email will be sent to ${contact.email}`;
                            } else {
                              description = `As you are in a test drive, you need to set a test drive contact in order to send emails, please click in the button below to access your contact page and set one`;
                            }

                            modal.pushModal(
                              <Warning
                                icon={"./icons/modal/email.svg"}
                                title={`Test Drive Account`}
                                description={description}
                                buttonsName={
                                  exists ? ["Cancel", "Send"] : ["Cancel", "Ok"]
                                }
                                onOkClicked={() => {
                                  if (exists) {
                                    handleScheduleOrSend();
                                    modal.popAll();
                                  } else {
                                    modal.popModal();
                                    router.replace("/contacts");
                                  }
                                }}
                                onCancelClicked={() => modal.popAll()}
                              />
                            );
                          } else {
                            handleScheduleOrSend();
                          }
                        }}
                        disable={!recipient.count}
                      >
                        {scheduleDate ? "Schedule" : "Send"}
                      </StyledButton>
                    )}
                </Fragment>
              ) : (
                <StyledButton
                  css={css`
                    width: 124px;
                    height: 46px;
                  `}
                  onClick={handleFinishClick}
                >
                  {scheduleDate ? "Reschedule" : "Send Now"}
                </StyledButton>
              )}
            </Fragment>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ScheduleBar;
